/**
 * This file contains list of all the natively supported attributes for <input/> and <textarea/> tag.
 * We will use these to filter non-required props in following files:
 * FormTextInput.js
 * InputNumberFieldV2.js
 * FormTextAreaInput.js
 */

const ariaAttributes = [
  /** Identifies the currently active element when DOM focus is on a composite widget, textbox, group, or application. */
  "aria-activedescendant",
  /** Indicates whether assistive technologies will present all, or only parts of, the changed region based on the change notifications defined by the aria-relevant attribute. */
  "aria-atomic",
  /**
   * Indicates whether inputting text could trigger display of one or more predictions of the user's intended value for an input and specifies how predictions would be
   * presented if they are made.
   */
  "aria-autocomplete",
  /** Indicates an element is being modified and that assistive technologies MAY want to wait until the modifications are complete before exposing them to the user. */
  "aria-busy",
  /**
   * Indicates the current "checked" state of checkboxes, radio buttons, and other widgets.
   */
  "aria-checked",
  /**
   * Defines the total number of columns in a table, grid, or treegrid.
   */
  "aria-colcount",
  /**
   * Defines an element's column index or position with respect to the total number of columns within a table, grid, or treegrid.
   */
  "aria-colindex",
  /**
   * Defines the number of columns spanned by a cell or gridcell within a table, grid, or treegrid.
   */
  "aria-colspan",
  /**
   * Identifies the element (or elements) whose contents or presence are controlled by the current element.
   */
  "aria-controls",
  /** Indicates the element that represents the current item within a container or set of related elements. */
  "aria-current",
  /**
   * Identifies the element (or elements) that describes the object.
   */
  "aria-describedby",
  /**
   * Identifies the element that provides a detailed, extended description for the object.
   */
  "aria-details",
  /**
   * Indicates that the element is perceivable but disabled, so it is not editable or otherwise operable.
   */
  "aria-disabled",
  /**
   * Indicates what functions can be performed when a dragged object is released on the drop target.
   */
  "aria-dropeffect",
  /**
   * Identifies the element that provides an error message for the object.
   */
  "aria-errormessage",
  /** Indicates whether the element, or another grouping element it controls, is currently expanded or collapsed. */
  "aria-expanded",
  /**
   * Identifies the next element (or elements) in an alternate reading order of content which, at the user's discretion,
   * allows assistive technology to override the general default of reading in document source order.
   */
  "aria-flowto",
  /**
   * Indicates an element's "grabbed" state in a drag-and-drop operation.
   */
  "aria-grabbed",
  /** Indicates the availability and type of interactive popup element, such as menu or dialog, that can be triggered by an element. */
  "aria-haspopup",
  /**
   * Indicates whether the element is exposed to an accessibility API.
   */
  "aria-hidden",
  /**
   * Indicates the entered value does not conform to the format expected by the application.
   */
  "aria-invalid",
  /** Indicates keyboard shortcuts that an author has implemented to activate or give focus to an element. */
  "aria-keyshortcuts",
  /**
   * Defines a string value that labels the current element.
   */
  "aria-label",
  /**
   * Identifies the element (or elements) that labels the current element.
   */
  "aria-labelledby",
  /** Defines the hierarchical level of an element within a structure. */
  "aria-level",
  /** Indicates that an element will be updated, and describes the types of updates the user agents, assistive technologies, and user can expect from the live region. */
  "aria-live",
  /** Indicates whether an element is modal when displayed. */
  "aria-modal",
  /** Indicates whether a text box accepts multiple lines of input or only a single line. */
  "aria-multiline",
  /** Indicates that the user may select more than one item from the current selectable descendants. */
  "aria-multiselectable",
  /** Indicates whether the element's orientation is horizontal, vertical, or unknown/ambiguous. */
  "aria-orientation",
  /**
   * Identifies an element (or elements) in order to define a visual, functional, or contextual parent/child relationship
   * between DOM elements where the DOM hierarchy cannot be used to represent the relationship.
   */
  "aria-owns",
  /**
   * Defines a short hint (a word or short phrase) intended to aid the user with data entry when the control has no value.
   * A hint could be a sample value or a brief description of the expected format.
   */
  "aria-placeholder",
  /**
   * Defines an element's number or position in the current set of listitems or treeitems. Not required if all elements in the set are present in the DOM.
   */
  "aria-posinset",
  /**
   * Indicates the current "pressed" state of toggle buttons.
   */
  "aria-pressed",
  /**
   * Indicates that the element is not editable, but is otherwise operable.
   */
  "aria-readonly",
  /**
   * Indicates what notifications the user agent will trigger when the accessibility tree within a live region is modified.
   */
  "aria-relevant",
  /** Indicates that user input is required on the element before a form may be submitted. */
  "aria-required",
  /** Defines a human-readable, author-localized description for the role of an element. */
  "aria-roledescription",
  /**
   * Defines the total number of rows in a table, grid, or treegrid.
   */
  "aria-rowcount",
  /**
   * Defines an element's row index or position with respect to the total number of rows within a table, grid, or treegrid.
   */
  "aria-rowindex",
  /**
   * Defines the number of rows spanned by a cell or gridcell within a table, grid, or treegrid.
   */
  "aria-rowspan",
  /**
   * Indicates the current "selected" state of various widgets.
   */
  "aria-selected",
  /**
   * Defines the number of items in the current set of listitems or treeitems. Not required if all elements in the set are present in the DOM.
   */
  "aria-setsize",
  /** Indicates if items in a table or grid are sorted in ascending or descending order. */
  "aria-sort",
  /** Defines the maximum allowed value for a range widget. */
  "aria-valuemax",
  /** Defines the minimum allowed value for a range widget. */
  "aria-valuemin",
  /**
   * Defines the current value for a range widget.
   */
  "aria-valuenow",
  /** Defines the human readable text alternative of aria-valuenow for a range widget. */
  "aria-valuetext",
];

const htmlAttributes = [
  // React-specific Attributes
  "defaultChecked",
  "defaultValue",
  "suppressContentEditableWarning",
  "suppressHydrationWarning",
  // Standard HTML Attributes
  "accessKey",
  "className",
  "contentEditable",
  "contextMenu",
  "dir",
  "draggable",
  "hidden",
  "id",
  "lang",
  "placeholder",
  "slot",
  "spellCheck",
  "style",
  "tabIndex",
  "title",
  "translate",
  // Unknown
  "radioGroup",
  // WAI-ARIA
  "role",
  // RDFa Attributes
  "about",
  "datatype",
  "inlist",
  "prefix",
  "property",
  "resource",
  "typeof",
  "vocab",
  // Non-standard Attributes
  "autoCapitalize",
  "autoCorrect",
  "autoSave",
  "color",
  "itemProp",
  "itemScope",
  "itemType",
  "itemID",
  "itemRef",
  "results",
  "security",
  "unselectable",
  "inputMode",
  "is",
];

const domAttributes = [
  "children",
  "dangerouslySetInnerHTML",
  // Clipboard Events
  "onCopy",
  "onCopyCapture",
  "onCut",
  "onCutCapture",
  "onPaste",
  "onPasteCapture",
  // Composition Events
  "onCompositionEnd",
  "onCompositionEndCapture",
  "onCompositionStart",
  "onCompositionStartCapture",
  "onCompositionUpdate",
  "onCompositionUpdateCapture",
  // Focus Events
  "onFocus",
  "onFocusCapture",
  "onBlur",
  "onBlurCapture",
  // Form Events
  "onChange",
  "onChangeCapture",
  "onBeforeInput",
  "onBeforeInputCapture",
  "onInput",
  "onInputCapture",
  "onReset",
  "onResetCapture",
  "onSubmit",
  "onSubmitCapture",
  "onInvalid",
  "onInvalidCapture",
  // Image Events
  "onLoad",
  "onLoadCapture",
  "onError",
  "onErrorCapture",
  // Keyboard Events
  "onKeyDown",
  "onKeyDownCapture",
  "onKeyPress",
  "onKeyPressCapture",
  "onKeyUp",
  "onKeyUpCapture",
  // Media Events
  "onAbort",
  "onAbortCapture",
  "onCanPlay",
  "onCanPlayCapture",
  "onCanPlayThrough",
  "onCanPlayThroughCapture",
  "onDurationChange",
  "onDurationChangeCapture",
  "onEmptied",
  "onEmptiedCapture",
  "onEncrypted",
  "onEncryptedCapture",
  "onEnded",
  "onEndedCapture",
  "onLoadedData",
  "onLoadedDataCapture",
  "onLoadedMetadata",
  "onLoadedMetadataCapture",
  "onLoadStart",
  "onLoadStartCapture",
  "onPause",
  "onPauseCapture",
  "onPlay",
  "onPlayCapture",
  "onPlaying",
  "onPlayingCapture",
  "onProgress",
  "onProgressCapture",
  "onRateChange",
  "onRateChangeCapture",
  "onSeeked",
  "onSeekedCapture",
  "onSeeking",
  "onSeekingCapture",
  "onStalled",
  "onStalledCapture",
  "onSuspend",
  "onSuspendCapture",
  "onTimeUpdate",
  "onTimeUpdateCapture",
  "onVolumeChange",
  "onVolumeChangeCapture",
  "onWaiting",
  "onWaitingCapture",
  // MouseEvents
  "onAuxClick",
  "onAuxClickCapture",
  "onClick",
  "onClickCapture",
  "onContextMenu",
  "onContextMenuCapture",
  "onDoubleClick",
  "onDoubleClickCapture",
  "onDrag",
  "onDragCapture",
  "onDragEnd",
  "onDragEndCapture",
  "onDragEnter",
  "onDragEnterCapture",
  "onDragExit",
  "onDragExitCapture",
  "onDragLeave",
  "onDragLeaveCapture",
  "onDragOver",
  "onDragOverCapture",
  "onDragStart",
  "onDragStartCapture",
  "onDrop",
  "onDropCapture",
  "onMouseDown",
  "onMouseDownCapture",
  "onMouseEnter",
  "onMouseLeave",
  "onMouseMove",
  "onMouseMoveCapture",
  "onMouseOut",
  "onMouseOutCapture",
  "onMouseOver",
  "onMouseOverCapture",
  "onMouseUp",
  "onMouseUpCapture",
  // Selection Events
  "onSelect",
  "onSelectCapture",
  // Touch Events
  "onTouchCancel",
  "onTouchCancelCapture",
  "onTouchEnd",
  "onTouchEndCapture",
  "onTouchMove",
  "onTouchMoveCapture",
  "onTouchStart",
  "onTouchStartCapture",
  // Pointer Events
  "onPointerDown",
  "onPointerDownCapture",
  "onPointerMove",
  "onPointerMoveCapture",
  "onPointerUp",
  "onPointerUpCapture",
  "onPointerCancel",
  "onPointerCancelCapture",
  "onPointerEnter",
  "onPointerEnterCapture",
  "onPointerLeave",
  "onPointerLeaveCapture",
  "onPointerOver",
  "onPointerOverCapture",
  "onPointerOut",
  "onPointerOutCapture",
  "onGotPointerCapture",
  "onGotPointerCaptureCapture",
  "onLostPointerCapture",
  "onLostPointerCaptureCapture",
  // UI Events
  "onScroll",
  "onScrollCapture",
  // Wheel Events
  "onWheel",
  "onWheelCapture",
  // Animation Events
  "onAnimationStart",
  "onAnimationStartCapture",
  "onAnimationEnd",
  "onAnimationEndCapture",
  "onAnimationIteration",
  "onAnimationIterationCapture",
  // Transition Events
  "onTransitionEnd",
  "onTransitionEndCapture",
];

export const inputHTMLAttributes = [
  "accept",
  "alt",
  "autoComplete",
  "autoFocus",
  "capture",
  "checked",
  "crossOrigin",
  "disabled",
  "enterKeyHint",
  "form",
  "formAction",
  "formEncType",
  "formMethod",
  "formNoValidate",
  "formTarget",
  "height",
  "list",
  "max",
  "maxLength",
  "min",
  "minLength",
  "multiple",
  "name",
  "pattern",
  "placeholder",
  "readOnly",
  "required",
  "size",
  "src",
  "step",
  "type",
  "value",
  "width",
  "onChange",
  "data-test-id",
  "testId",
  ...ariaAttributes,
  ...domAttributes,
  ...htmlAttributes,
];

export const textareaHTMLAttributes = [
  "maxRows",
  "minRows",
  "onHeightChange",
  "cacheMeasurements",
  "style",
  "autoComplete",
  "autoFocus",
  "cols",
  "dirName",
  "disabled",
  "form",
  "maxLength",
  "minLength",
  "name",
  "placeholder",
  "readOnly",
  "required",
  "rows",
  "value",
  "wrap",
  "onChange",
  ...ariaAttributes,
  ...domAttributes,
  ...htmlAttributes,
];
